<template>
  <div class="d-flex flex-column" style="gap: 24px; border-radius: 8px">
    <div class="container-plot">
      <div>
        <v-row no-gutters align="center" justify="space-between">
          <span class="body-bold-medium neutral-20-dark--text">
            {{ $t('message.pages.analyticsV3.reference.referrals') }}
          </span>

          <span class="body-regular-smaller neutral-40-light--text">
            {{
              $t('message.pages.analyticsV3.pages.devices.updatedAgo', {
                time: lastUpdatedMinutes,
              })
            }}
          </span>
        </v-row>

        <span class="body-regular-smaller neutral-40-light--text">
          {{ $t('message.pages.analyticsV3.reference.helpReferrals') }}
          <a
            type="secondary"
            target="_blank"
            @click="handleGoToAnalyticsArticle"
          >
            <span class="body-bold-smaller knowMore mr-1">{{
              $t('message.actions.knowMore')
            }}</span>
            <v-icon size="12" color="neutral-30-base">mdi-open-in-new</v-icon>
          </a>
        </span>
      </div>

      <v-row no-gutters align="center" justify="space-between">
        <div class="d-flex flex-wrap align-center">
          <div
            class="metric-btn d-flex align-center"
            v-for="(item, index) in chartSeries"
            :key="index"
          >
            <div
              class="d-flex align-center pa-1"
              :class="{
                'metric-btn--opacity':
                  !!filterByMetric && filterByMetric !== item.name,
              }"
              style="gap: 8px"
              @click="() => handleFilterByMetric(item.name)"
            >
              <v-badge
                class="my-badge-scope"
                inline
                :color="metricColors[item.name]"
              />

              <div class="d-flex align-center" style="user-select: none">
                <span class="body-regular-smaller neutral-20-dark--text mr-1">
                  {{ $t(`message.pages.analyticsV3.reference.${item.name}`) }}:
                </span>

                <v-skeleton-loader
                  v-if="loading"
                  width="36"
                  height="14"
                  type="image"
                />

                <span v-else class="body-regular-smaller neutral-20-dark--text">
                  {{ Number(sumPrecise(item.data)).toLocaleString('pt-BR') }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <base-select
            v-model="showPlotBy"
            :items="showPlotByPlaceholders"
            :menu-props="{ top: false, offsetY: true }"
            item-text="name"
            item-value="value"
            dense
            hide-details
            attach
            :style="`width: ${$i18next.language === 'es' ? 169 : 124}px`"
          />
        </div>
      </v-row>

      <v-skeleton-loader v-if="loading" type="image" height="100%" />

      <apexchart
        v-else
        class="chart-full"
        ref="referencesChart"
        type="line"
        height="300"
        width="100%"
        :options="getChartOptions"
        :series="chartSeries"
        :key="locale"
      />
      <apexchart
        v-if="chartSeries.length > 0"
        class="pdf-export-chart"
        ref="fakeChart"
        type="line"
        height="300"
        width="894"
        :options="pdfOptions"
        :series="chartSeries"
        :key="'fake'"
      />
    </div>

    <div v-if="isCapturingPng" class="capture-clone">
      <div ref="chartContainerForCapture" style="width: 100%; height: 100%; background: white; padding: 24px;">
        <div>
          <v-row no-gutters>
            <span class="body-bold-medium" style="color: #282931">
              {{ $t('message.pages.analyticsV3.reference.referrals') }}
            </span>
          </v-row>
          <span class="body-regular-smaller" style="color: #636578">
            {{ $t('message.pages.analyticsV3.reference.helpReferrals') }}
          </span>
        </div>
        <v-row no-gutters class="mt-6">
          <div class="d-flex flex-wrap align-center">
            <div class="metric-btn d-flex align-center" v-for="(item, index) in chartSeries" :key="index">
              <div class="d-flex align-center pa-1" style="gap: 8px">
                <v-badge class="my-badge-scope" inline :color="metricColors[item.name]" />
                <div class="d-flex align-center" style="user-select: none">
                  <span class="body-regular-smaller mr-1"  style="color: #282931">
                    {{ $t(`message.pages.analyticsV3.reference.${item.name}`) }}:
                  </span>
                  <span class="body-regular-smaller"  style="color: #282931">
                    {{ Number(sumPrecise(item.data)).toLocaleString('pt-BR') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </v-row>
        <apexchart
          v-if="chartSeries.length > 0"
          class="chart-full"
          type="line"
          height="300"
          width="100%"
          :options="pdfOptions"
          :series="chartSeries"
          :key="locale + '-clone'"
        />
      </div>
    </div>


    <div class="container-table">
      <v-input
        class="search-bar"
        v-model="searchByUrl"
        size="small"
        hide-details
        :placeholder="$t('message.pages.analyticsV3.reference.searchurl')"
        @input="doItemsSearch"
        ><template v-slot:prepend-inner>
          <v-icon class="neutral-60--text" size="16"> mdi-magnify </v-icon>
        </template></v-input
      >

      <BaseTable
        :items="
          searchByUrl.length > 0 ? filteredRankingItems : aggregatedVideosToList
        "
        :columns="visibleTableColumns"
        :items-per-page="10"
        :show-pagination="true"
        :is-searching="searchByUrl.length > 0"
        :loading="loading"
        :showActionColumn="true"
        @clear-search="clearSearch"
        @update-visible-columns="currentlyVisibleColumns = $event"
        :showTotalLabel="false"
        noPointer
      >
        <template #titleCell="{ row }">
          <span class="truncate-cell" :title="row.title">
            {{ row.title.toLowerCase() !== 'null' ? row.title : 'N/A' }}
          </span>
        </template>
        <template #playCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{
              Number(row.play).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}
          </span>
        </template>
        <template #unique_playCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{
              Number(row.unique_play).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}
          </span>
        </template>
        <template #unique_viewCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{
              Number(row.unique_view).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}
          </span>
        </template>
        <template #viewCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{
              Number(row.view).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}
          </span>
        </template>
        <template #play_rateCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{ row.play_rate === 'N/A' ? 'N/A' : row.play_rate + '%' }}
          </span>
        </template>
      </BaseTable>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { enUS, ptBR, es as esES } from 'date-fns/locale'
import { mapState, mapMutations } from 'vuex'
import VueApexCharts from 'vue-apexcharts'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import BaseSelect from '@/components/BaseComponents/BaseSelect.vue'
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import Fuse from 'fuse.js'
import { exportToCsv } from '@/utils/csvExporter.js'
import { exportToPng } from '@/utils/pngExporter.js'
import { generatePdfBlob } from '@/utils/pdfExporter.js'
import currencyMixin from '../../mixins/currencyMixin'
import { dataFormatter } from '@/utils/dataFormatter'

export default {
  name: 'Reference',
  components: {
    apexchart: VueApexCharts,
    BaseSelect,
    BaseTable,
  },
  mixins: [currencyMixin],
  data() {
    return {
      isCapturingPng: false,
      loading: false,
      noInfoFound: false,
      aggregatedDataToPlot: [],
      aggregatedVideosToList: [],
      filteredRankingItems: [],
      lastUpdatedAt: 0,
      lastUpdatedTimeout: null,
      chartSeries: [
        { name: 'play' },
        { name: 'unique_play' },
        { name: 'view' },
        { name: 'unique_view' },
      ],
      chartCategories: [],
      showPlotBy: 'daily',
      filterByMetric: null,
      searchByUrl: '',
      metricColors: {
        play: '#3F55EE',
        unique_play: '#20C1A4',
        view: '#FF882B',
        unique_view: '#9D1DA0',
      },
      analyticsLinks: {
        pt: 'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
        en: 'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        es: 'https://help.pandavideo.com/es/article/como-funcionan-las-metricas-de-analytics-en-panda-video-cpkcfv/',
      },
      currentlyVisibleColumns: [],
    }
  },
  computed: {
    ...mapState({
      dateRange: (state) => state.analytics.filters.dateRange,
      selectedVideos: (state) =>
        state.analytics.filters.filterVideos.selectedVideos,
    }),
    isRangeMoreThan30Days() {
      if (!this.dateRange || !this.dateRange.start || !this.dateRange.end)
        return false
      const start = new Date(this.dateRange.start)
      const end = new Date(this.dateRange.end)
      const diff = (end - start) / (1000 * 60 * 60 * 24)
      return diff >= 30
    },
    rotateAlways() {
      return this.isRangeMoreThan30Days && this.showPlotBy === 'daily'
    },
    locale() {
      return this.$i18next.resolvedLanguage
    },
    showPlotByPlaceholders() {
      return [
        {
          value: 'daily',
          name: this.$i18next.t('message.pages.analyticsV3.daily'),
        },
        {
          value: 'weekly',
          name: this.$i18next.t('message.pages.analyticsV3.weekly'),
        },
        {
          value: 'monthly',
          name: this.$i18next.t('message.pages.analyticsV3.monthly'),
        },
      ]
    },
    baseTableColumns() {
      return [
        {
          text: this.$t('message.pages.analyticsV3.reference.url'),
          value: 'title',
          align: 'start',
          width: '320px',
        },
        {
          text: this.$t('message.pages.analyticsV3.reference.play'),
          value: 'play',
          align: 'end',
          tooltip: this.$t('message.pages.analyticsV3.reference.playTooltip'),
        },
        {
          text: this.$t('message.pages.analyticsV3.reference.unique_play'),
          value: 'unique_play',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.reference.unique_playTooltip'
          ),
        },
        {
          text: this.$t('message.pages.analyticsV3.reference.view'),
          value: 'view',
          align: 'end',
          tooltip: this.$t('message.pages.analyticsV3.reference.viewTooltip'),
        },
        {
          text: this.$t('message.pages.analyticsV3.reference.unique_view'),
          value: 'unique_view',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.reference.unique_viewTooltip'
          ),
        },
        {
          text: this.$t('message.pages.analyticsV3.reference.playRate'),
          value: 'play_rate',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.reference.playRateTooltip'
          ),
          formatter: (value) => `${value}%`,
        },
      ]
    },
    visibleTableColumns() {
      return this.baseTableColumns.map((col) => ({
        ...col,
        width: 100 / this.baseTableColumns.length + '%',
      }))
    },
    getChartOptions() {
      const textColor = this.$vuetify.theme.currentTheme['neutral-40-light']
      const gridColor = this.$vuetify.theme.currentTheme['neutral-95-lightest']
      const isDark = this.$vuetify.theme.dark
      const localeMap = { pt: ptBR, es: esES, en: enUS }
      const localeDateFns = localeMap[this.locale] || enUS

      return {
        chart: {
          type: 'line',
          height: 300,
          width: '100%',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: { width: 2 },
        grid: {
          borderColor: gridColor,
          strokeDashArray: 0,
          yaxis: {
            lines: {
              show: true,
              color: gridColor,
            },
          },
          xaxis: {
            lines: {
              show: false,
            },
          },
        },
        xaxis: {
          tickAmount: 30,
          categories: this.chartCategories,
          tooltip: {
            enabled: false,
          },
          axisBorder: {
            show: true,
            color: gridColor,
          },
          axisTicks: {
            show: true,
            color: gridColor,
          },
          labels: {
            style: {
              fontFamily: 'Rubik, sans-serif',
              fontSize: '11px',
              fontWeight: 400,
              colors: textColor,
            },
            formatter: (value) => {
              if (!value) return value
              const showBy = this.showPlotBy
              if (showBy === 'daily') {
                return dataFormatter(value, localeDateFns)
              }
              if (showBy === 'weekly') {
                const [year, week] = value.split('-W')
                const firstDay = this.getDateOfISOWeek(+week, +year)
                const lastDay = new Date(firstDay)
                lastDay.setDate(firstDay.getDate() + 6)
                const sameMonth = firstDay.getMonth() === lastDay.getMonth()
                const startStr = format(firstDay, sameMonth ? 'dd' : 'dd/MM', {
                  locale: localeDateFns,
                })
                const endStr = format(lastDay, 'dd/MM', {
                  locale: localeDateFns,
                })
                return `${startStr} - ${endStr}`
              }
              if (showBy === 'monthly') {
                const [year, month] = value.split('-').map(Number)
                const date = new Date(year, month - 1, 1)
                const monthStr = format(date, 'MMMM', { locale: localeDateFns })

                return monthStr.charAt(0).toUpperCase() + monthStr.slice(1)
              }
              return value
            },
            rotateAlways: this.rotateAlways,
          },
        },
        yaxis: {
          labels: {
            style: {
              fontFamily: 'Rubik, sans-serif',
              fontSize: '11px',
              fontWeight: 400,
              colors: textColor,
            },
            formatter: (value) => {
              const locale = this.locale === 'pt' ? 'pt-BR' : 'en-US'
              if (value >= 1_000_000) {
                const n = value / 1_000_000
                const isInt = Number.isInteger(n)
                return `${n.toLocaleString(locale, {
                  minimumFractionDigits: isInt ? 0 : 1,
                  maximumFractionDigits: 1,
                })}M`
              }
              if (value >= 1_000) {
                const n = value / 1_000
                const isInt = Number.isInteger(n)
                return `${n.toLocaleString(locale, {
                  minimumFractionDigits: isInt ? 0 : 1,
                  maximumFractionDigits: 1,
                })}k`
              }
              return value.toLocaleString(locale)
            },
          },
        },
        legend: { show: false },
        tooltip: {
          theme: isDark ? 'dark' : 'light',
          y: {
            title: {
              formatter: (value) => {
                return this.$t(`message.pages.analyticsV3.reference.${value}`)
              },
            },
          },
        },
      }
    },
    pdfOptions() {
      const textColor = '#636578'
      const gridColor = '#E0E0E0'
      const isDark = this.$vuetify.theme.dark
      const lang = this.locale || 'en'
      const localeMap = { pt: ptBR, es: esES, en: enUS }
      const localeDateFns = localeMap[lang] || enUS

      return {
        chart: {
          type: 'line',
          height: 300,
          width: 894,
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: { width: 2 },
        grid: {
          borderColor: gridColor,
          strokeDashArray: 0,
          yaxis: {
            lines: {
              show: true,
              color: gridColor,
            },
          },
          xaxis: {
            lines: {
              show: false,
            },
          },
        },
        xaxis: {
          tickAmount: 30,
          categories: this.chartCategories,
          tooltip: {
            enabled: false,
          },
          axisBorder: {
            show: true,
            color: gridColor,
          },
          axisTicks: {
            show: true,
            color: gridColor,
          },
          labels: {
            style: {
              fontFamily: 'Rubik, sans-serif',
              fontSize: '11px',
              fontWeight: 400,
              colors: textColor,
            },
            formatter: (value) => {
              if (!value) return value
              const showBy = this.showPlotBy
              if (showBy === 'daily') {
                return dataFormatter(value, localeDateFns)
              }
              if (showBy === 'weekly') {
                const [year, week] = value.split('-W')
                const firstDay = this.getDateOfISOWeek(+week, +year)
                const lastDay = new Date(firstDay)
                lastDay.setDate(firstDay.getDate() + 6)
                const sameMonth = firstDay.getMonth() === lastDay.getMonth()
                const startStr = format(firstDay, sameMonth ? 'dd' : 'dd/MM', {
                  locale: localeDateFns,
                })
                const endStr = format(lastDay, 'dd/MM', {
                  locale: localeDateFns,
                })
                return `${startStr} - ${endStr}`
              }
              if (showBy === 'monthly') {
                const [year, month] = value.split('-').map(Number)
                const date = new Date(year, month - 1, 1)
                const monthStr = format(date, 'MMMM', { locale: localeDateFns })

                return monthStr.charAt(0).toUpperCase() + monthStr.slice(1)
              }
              return value
            },
            rotateAlways: this.rotateAlways,
          },
        },
        yaxis: {
          labels: {
            style: {
              fontFamily: 'Rubik, sans-serif',
              fontSize: '11px',
              fontWeight: 400,
              colors: textColor,
            },
            formatter: (value) => {
              const locale = lang === 'pt' ? 'pt-BR' : 'en-US'
              if (value >= 1_000_000) {
                const n = value / 1_000_000
                const isInt = Number.isInteger(n)
                return `${n.toLocaleString(locale, {
                  minimumFractionDigits: isInt ? 0 : 1,
                  maximumFractionDigits: 1,
                })}M`
              }
              if (value >= 1_000) {
                const n = value / 1_000
                const isInt = Number.isInteger(n)
                return `${n.toLocaleString(locale, {
                  minimumFractionDigits: isInt ? 0 : 1,
                  maximumFractionDigits: 1,
                })}k`
              }
              return value.toLocaleString(locale)
            },
          },
        },
        legend: { show: false },
        tooltip: {
          theme: isDark ? 'dark' : 'light',
          y: {
            title: {
              formatter: (value) => {
                return this.$t(`message.pages.analyticsV3.reference.${value}`)
              },
            },
          },
        },
      }
    },
    lastUpdatedMinutes() {
      return Math.floor(this.lastUpdatedAt / 60) || 1
    },
  },
  methods: {
    ...mapMutations('analytics', ['UPDATE_ERROR_STATE']),
    async fetchGlobalMetrics() {
      this.clearTimeIntervals()
      this.loading = true
      try {
        const res = await newAnalyticsApi.getReferencesMetrics(
          format(this.dateRange.start, 'yyyy-MM-dd'),
          format(this.dateRange.end, 'yyyy-MM-dd')
        )

        if (!Object.keys(res.data).length) {
          this.noInfoFound = true
          return
        }

        const aggregatedData = this.aggregateDataByDate(res.data)
        const aggregatedVideos = this.aggregateDataByVideoTotal(res.data)

        this.aggregatedDataToPlot = aggregatedData
        this.aggregatedVideosToList = aggregatedVideos

        this.lastUpdatedTimeout = setInterval(() => {
          this.lastUpdatedAt += 1
        }, 1000)
      } catch (e) {
        console.error('Erro ao buscar dados de referência:', e)
        this.UPDATE_ERROR_STATE(true)
      } finally {
        this.loading = false
      }
    },
    async getSelectedReferencesInfo() {
      this.clearTimeIntervals()
      this.loading = true
      try {
        let referencesResponses = []
        for (let video of this.selectedVideos) {
          let ret = await newAnalyticsApi.getSingleReferencesMetrics(
            video.videoId,
            format(this.dateRange.start, 'yyyy-MM-dd'),
            format(this.dateRange.end, 'yyyy-MM-dd')
          )
          referencesResponses.push(ret.data)
        }

        const mergedResponses = this.mergeResponses(referencesResponses)

        const aggregatedData = this.aggregateDataByDate(mergedResponses)
        const aggregatedVideos = this.aggregateDataByVideoTotal(mergedResponses)

        this.aggregatedDataToPlot = aggregatedData
        this.aggregatedVideosToList = aggregatedVideos

        this.lastUpdatedTimeout = setInterval(() => {
          this.lastUpdatedAt += 1
        }, 1000)
      } catch (error) {
        this.UPDATE_ERROR_STATE(true)
      } finally {
        this.loading = false
      }
    },
    mergeResponses(responses) {
      const merged = {}

      responses.forEach((response) => {
        Object.entries(response).forEach(([url, timestamps]) => {
          const normalizedUrl = url.replace(/\/+$/, '')

          if (!merged[normalizedUrl]) {
            merged[normalizedUrl] = {}
          }

          Object.entries(timestamps).forEach(([ts, metrics]) => {
            const dateKey = ts.split(' ')[0]

            if (!merged[normalizedUrl][dateKey]) {
              merged[normalizedUrl][dateKey] = {
                play: 0,
                view: 0,
                unique_play: 0,
                unique_view: 0,
              }
            }

            Object.entries(metrics).forEach(([key, value]) => {
              merged[normalizedUrl][dateKey][key] += value
            })
          })
        })
      })

      return merged
    },
    aggregateDataByDate(data) {
      const dailyMap = {}
      const weeklyMap = {}
      const monthlyMap = {}

      for (const source in data) {
        const dates = data[source]
        for (const dateStr in dates) {
          const stats = dates[dateStr]
          const date = new Date(dateStr)

          const dailyKey = dateStr
          const weekNumber = this.getWeekNumber(date)
          const weeklyKey = `${date.getFullYear()}-W${weekNumber}`
          const monthlyKey = `${date.getFullYear()}-${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}`

          if (!dailyMap[dailyKey]) {
            dailyMap[dailyKey] = {
              date: dailyKey,
              view: 0,
              play: 0,
              unique_view: 0,
              unique_play: 0,
            }
          }
          dailyMap[dailyKey].view += stats.view || 0
          dailyMap[dailyKey].play += stats.play || 0
          dailyMap[dailyKey].unique_view += stats.unique_view || 0
          dailyMap[dailyKey].unique_play += stats.unique_play || 0

          if (!weeklyMap[weeklyKey]) {
            weeklyMap[weeklyKey] = {
              date: weeklyKey,
              view: 0,
              play: 0,
              unique_view: 0,
              unique_play: 0,
            }
          }
          weeklyMap[weeklyKey].view += stats.view || 0
          weeklyMap[weeklyKey].play += stats.play || 0
          weeklyMap[weeklyKey].unique_view += stats.unique_view || 0
          weeklyMap[weeklyKey].unique_play += stats.unique_play || 0

          if (!monthlyMap[monthlyKey]) {
            monthlyMap[monthlyKey] = {
              date: monthlyKey,
              view: 0,
              play: 0,
              unique_view: 0,
              unique_play: 0,
            }
          }
          monthlyMap[monthlyKey].view += stats.view || 0
          monthlyMap[monthlyKey].play += stats.play || 0
          monthlyMap[monthlyKey].unique_view += stats.unique_view || 0
          monthlyMap[monthlyKey].unique_play += stats.unique_play || 0
        }
      }

      return {
        daily: (() => {
          const start = new Date(this.dateRange.start)
          const end = new Date(this.dateRange.end)
          const days = []
          let d = new Date(start)
          while (d <= end) {
            days.push(format(d, 'yyyy-MM-dd'))
            d.setDate(d.getDate() + 1)
          }
          return days.map((dateStr) => {
            return (
              dailyMap[dateStr] || {
                date: dateStr,
                view: 0,
                play: 0,
                unique_view: 0,
                unique_play: 0,
              }
            )
          })
        })(),
        weekly: Object.values(weeklyMap).sort((a, b) =>
          a.date.localeCompare(b.date)
        ),
        monthly: Object.values(monthlyMap).sort((a, b) =>
          a.date.localeCompare(b.date)
        ),
      }
    },
    aggregateDataByVideoTotal(data) {
      const aggregated = {}

      for (const source in data) {
        if (!aggregated[source]) {
          aggregated[source] = {
            title: source,
            view: 0,
            play: 0,
            unique_view: 0,
            unique_play: 0,
            play_rate: '0.0',
          }
        }

        const dates = data[source]
        for (const date in dates) {
          const stats = dates[date]

          aggregated[source].view += stats.view || 0
          aggregated[source].play += stats.play || 0
          aggregated[source].unique_view += stats.unique_view || 0
          aggregated[source].unique_play += stats.unique_play || 0
        }
      }
      // Corrigir play_rate igual à tela de plays
      for (const source in aggregated) {
        const view = aggregated[source].view
        const play = aggregated[source].play
        if (view > 0) {
          aggregated[source].play_rate = ((play * 100) / view).toFixed(1)
        } else if (play > 0) {
          aggregated[source].play_rate = '100'
        } else {
          aggregated[source].play_rate = 'N/A'
        }
      }

      return Object.entries(aggregated).map(([video, stats]) => ({
        video,
        ...stats,
      }))
    },
    buildSeriesFromAggregatedData(data) {
      const props = ['play', 'unique_play', 'view', 'unique_view']

      return props.map((prop) => {
        const hex = this.metricColors[prop]
        const rgb = hex
          .replace('#', '')
          .match(/.{1,2}/g)
          .map((x) => parseInt(x, 16))

        const color =
          this.filterByMetric && this.filterByMetric !== prop
            ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.2)`
            : hex

        return {
          name: prop,
          data: data.map((entry) => entry[prop] || 0),
          color,
        }
      })
    },
    getDateOfISOWeek(week, year) {
      const simple = new Date(year, 0, 1 + (week - 1) * 7)
      const dow = simple.getDay()
      const ISOweekStart = simple
      if (dow <= 4) {
        ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1)
      } else {
        ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay())
      }
      return ISOweekStart
    },
    getWeekNumber(date) {
      const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      )
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
    },
    getCategoriesFromData(data) {
      return data.map((entry) => entry.date)
    },
    sumPrecise(array) {
      if (!array) {
        return 0
      }

      return array.reduce((acc, val) => acc + parseFloat(val || 0), 0)
    },
    buildPlot(data) {
      this.chartSeries = this.buildSeriesFromAggregatedData(
        data[this.showPlotBy]
      )

      this.chartCategories = this.getCategoriesFromData(data[this.showPlotBy])
    },
    handleFilterByMetric(filter) {
      if (filter === this.filterByMetric) {
        this.filterByMetric = null
        return
      }

      this.filterByMetric = filter
    },
    handleGoToAnalyticsArticle() {
      const articleLink =
        this.analyticsLinks[this.$i18next.resolvedLanguage] ??
        this.analyticsLinks['en']
      window.open(articleLink, '_blank')
    },
    handleScroll(event) {
      const scrollTop = event.target.scrollTop
      this.isScrolled = scrollTop > 5
    },
    doItemsSearch() {
      let searchValue = this.searchByUrl
      if (searchValue && searchValue.length > 0) {
        let list = JSON.parse(JSON.stringify(this.aggregatedVideosToList))
        let fuseOptions = {
          keys: ['title'],
          includeScore: true,
          threshold: 0.4,
        }
        const fuse = new Fuse(list, fuseOptions)
        let searchResult = fuse.search(searchValue)
        const itemsResults = searchResult.map((result) => result.item)
        this.filteredRankingItems = itemsResults
      } else {
        this.filteredRankingItems = []
      }
    },
    clearSearch() {
      this.searchByUrl = ''
    },
    clearTimeIntervals() {
      clearInterval(this.lastUpdatedTimeout)
      this.lastUpdatedTimeout = null
      this.lastUpdatedAt = 0
    },
    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      const tableData =
        this.searchByUrl.length > 0
          ? this.filteredRankingItems
          : this.aggregatedVideosToList
      exportToCsv(
        `analytics-reference-${startDateFormatted}--${endDateFormatted}.csv`,
        this.currentlyVisibleColumns,
        tableData
      )
    },
    async generateAndDownloadPng() {
      this.isCapturingPng = true
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const elementToCapture = this.$refs.chartContainerForCapture
      if (elementToCapture) {
        try {
          const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
          const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
          const filename = `reference-${startDateFormatted}--${endDateFormatted}.png`
          await exportToPng(elementToCapture, filename)
        } catch (error) {
          this.UPDATE_ERROR_STATE(true)
        } finally {
          this.isCapturingPng = false
        }
      } else {
        console.error('Elemento para captura não foi encontrado.')
        this.isCapturingPng = false
      }
    },
    async generatePdf() {
      await this.$nextTick()

      const chartForPdf = this.$refs.fakeChart?.chart

      if (!chartForPdf) {
        console.error(
          'CRÍTICO: A instância do gráfico para PDF não foi encontrada. Verifique se a `viewChartSeries` tem dados.'
        )
        return
      }

      const headers = this.visibleTableColumns.map((col) => col.text)
      const dataToExport =
        this.searchByUrl.length > 0
          ? this.filteredRankingItems
          : this.aggregatedVideosToList
      const sortedDataForPdf = [...dataToExport].sort((a, b) => b.play - a.play)
      const body = sortedDataForPdf.map((item) => {
        return this.visibleTableColumns.map((col) => {
          if (col.formatter) {
            return col.formatter(item[col.value], item).replace(/="?|"?/g, '')
          }
          return item[col.value] || ''
        })
      })
      const chartHeaderData = {
        title: this.$t('message.pages.analyticsV3.reference.referrals'),
        description: this.$t(
          'message.pages.analyticsV3.reference.helpReferrals'
        ),
        metrics: this.chartSeries.map((item) => ({
          label: `${this.$t(
            `message.pages.analyticsV3.reference.${item.name}`
          )}:`,
          value: Number(this.sumPrecise(item.data)).toLocaleString('pt-BR'),
          color: this.metricColors[item.name],
        })),
      }

      const columnStyles = {
        0: {
          cellWidth: 'auto',
          overflow: 'ellipsize',
        },
        1: { cellWidth: 22 },
        2: { cellWidth: 22 },
        3: { cellWidth: 22 },
        4: { cellWidth: 22 },
        5: { cellWidth: 22 },
      }

      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      const filename = `references-${startDateFormatted}--${endDateFormatted}.pdf`

      const pdfBlob = await generatePdfBlob({
        chartHeaderData: chartHeaderData,
        chartInstance: chartForPdf,
        tableHeaders: headers,
        tableBody: body,
        columnStyles: columnStyles,
      })
      this.$root.$emit('pdf-ready', { blob: pdfBlob, filename: filename })
    },
  },
  watch: {
    aggregatedDataToPlot(newVal) {
      this.buildPlot(newVal)
    },
    dateRange() {
      if (this.selectedVideos?.length > 0) {
        this.getSelectedReferencesInfo()
        return
      }
      this.fetchGlobalMetrics()
    },
    showPlotBy() {
      this.buildPlot(this.aggregatedDataToPlot)
    },
    filterByMetric() {
      this.buildPlot(this.aggregatedDataToPlot)
    },
    locale() {
      this.buildPlot(this.aggregatedDataToPlot)
    },
    selectedVideos() {
      if (this.selectedVideos?.length > 0) {
        this.getSelectedReferencesInfo()
        return
      }

      this.fetchGlobalMetrics()
    },
  },
  mounted() {
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$on('trigger-pdf-export', this.generatePdf)
    if (this.selectedVideos?.length > 0) {
      this.getSelectedReferencesInfo()
      return
    }
    this.fetchGlobalMetrics()
  },
  beforeDestroy() {
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$off('trigger-pdf-export', this.generatePdf)
  },
}
</script>

<style lang="scss">
.container-plot {
  .v-skeleton-loader__image {
    height: 288px;
  }
}
</style>
<style lang="scss" scoped>
.container-plot {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 24px;
  gap: 24px;
  border-radius: 8px;
  background-color: varFigma(neutral-100-white);
}

.container-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 24px;
  border-radius: 8px;
  background-color: varFigma(neutral-100-white);
}

.chart-full {
  width: 100%;
  height: 300px;
  min-height: 300px;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.metric-btn {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  padding: 4px;
  :hover {
    background-color: varFigma(neutral-95-lightest);
    border-radius: 8px;
    cursor: pointer;
  }
}

.metric-btn--opacity {
  opacity: 0.2;
}

.truncate-cell {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>

<style lang="scss">
.my-badge-scope .v-badge__badge {
  height: 12px !important;
  width: 12px !important;
  min-width: 12px !important;
  max-height: 12px !important;
  padding: 0 !important;
  line-height: 12px !important;
  font-size: 0 !important;
}

.apexcharts-tooltip {
  background-color: varFigma(neutral-100-white) !important;
  color: varFigma(neutral-20-dark) !important;
  border: 1px solid varFigma(neutral-95-lightest) !important;
}

.apexcharts-tooltip-title {
  background-color: varFigma(neutral-95-lightest) !important;
  color: varFigma(neutral-20-dark) !important;
  font-weight: 500;
  padding: 6px 10px;
}

.pdf-export-chart {
  position: absolute;
  top: -9999px;
  left: -9999px;
  z-index: -1;
}

.capture-clone {
  position: absolute;
  top: -9999px;
  left: -9999px;
  pointer-events: none;
  height: 450px;
  width: 1200px; 
  background: white;
  box-sizing: border-box;
}

</style>
